import Markdown from 'react-markdown';
import type { DecoratedTask } from '@witmetrics/utils';
import { useDateFormatPreference, useDispatch } from '@witmetrics/data-store';
import { formatDateWithTime } from '@witmetrics/utils/time';
import { useAppState } from '@/providers/AppStateProvider';
import { useToggle } from '@/hooks/useToggle';
import Menu, { type MenuProps } from '@/components/Menu';
import Divider from '@/components/Divider';
import DeleteTaskDialog from '@/dialogs/DeleteTaskDialog';
import CheckBox from '@/components/CheckBox';
import TruncatedText from '@/components/TruncatedText';
import MenuFooter from './MenuFooter';
import AssignedTo from './AssignedTo';
import { classes, updateIsResolved } from './utils';

export type EventMenuProps = {
  open?: boolean;
  title: string;
  task: DecoratedTask;
  anchorTo: HTMLElement | null;
  anchorOrigin?: MenuProps['anchorOrigin'];
  transformOrigin?: MenuProps['transformOrigin'];
  onSelect: () => void;
  onEdit: () => void;
  onClose: () => void;
};

export default function EventMenu({
  open = true,
  title,
  task,
  anchorTo,
  anchorOrigin = {
    vertical: 'center',
    horizontal: 'right',
  },
  transformOrigin = {
    vertical: 'center',
    horizontal: 'left',
  },
  onSelect,
  onEdit,
  onClose,
}: EventMenuProps) {
  const dispatch = useDispatch();
  const { onApiError } = useAppState();
  const dateFormat = useDateFormatPreference();
  const [isDeleteWarningShown, toggleDeleteWarning] = useToggle();
  const [isUpdating, toggleUpdating] = useToggle();

  const handleToggleChecked = async (isResolved: boolean) => {
    try {
      toggleUpdating(true);
      dispatch.addTasks([await updateIsResolved(task.id, isResolved)]);
      toggleUpdating(false);
    } catch (err) {
      toggleUpdating(false);
      onApiError(err, 'Error saving task');
    }
  };

  const handleSelect = () => {
    onSelect();
    onClose();
  };

  const handleEdit = () => {
    onEdit();
    onClose();
  };

  const handleDelete = () => {
    toggleDeleteWarning(true);
  };

  return (
    <>
      <Menu
        anchorTo={anchorTo}
        open={open}
        className={classes.menu}
        anchorOrigin={anchorOrigin}
        transformOrigin={transformOrigin}
        onClose={onClose}>
        <div className={classes.wrapper}>
          <div className={classes.titleWrapper}>
            <CheckBox
              isUpdating={isUpdating}
              isChecked={task.isResolved}
              name="isResolved"
              onChange={({ isResolved }) => handleToggleChecked(isResolved)}
            />
            <h3 className={classes.dueAt}>
              {formatDateWithTime(task.dueAt, dateFormat)}
            </h3>
          </div>
          <Divider />
          <div className={classes.details}>
            <TruncatedText
              className={classes.title}
              tooltipPlacement="top"
              onClick={handleSelect}>
              {title}
            </TruncatedText>
            <span className={classes.description}>
              <Markdown>{task.description}</Markdown>
            </span>
          </div>
        </div>
        {task.assignedTo && (
          <>
            <Divider />
            <AssignedTo assignedTo={task.assignedTo} />
          </>
        )}
        <Divider />
        <MenuFooter onEdit={handleEdit} onDelete={handleDelete} />
      </Menu>
      {isDeleteWarningShown && (
        <DeleteTaskDialog
          taskId={task.id}
          onClose={() => toggleDeleteWarning(false)}
        />
      )}
    </>
  );
}
